import { clsx } from 'clsx';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Suspense, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useFeature } from '@optimizely/react-sdk';

import {
  chatBodyReadyAtom,
  globalOptiHeaderVisibleAtom,
  opalAuthAtom,
  opalOverlayInitialMessagingAtom,
  opalOverlayOpenAtom,
} from '@frontend/atoms';
import FullScreenLoader from '@frontend/components/FullScreenLoader';
import chatConfig from '@frontend/config/chat';
import { EVENT_NAME } from '@frontend/constants';
import { useOpalUser } from '@frontend/hooks/useOpalUser';
import { isDesktopApp } from '@frontend/utils/desktop';

import { useIsOnOverlayPage } from '../hooks/useIsOnOverlayPage';

import { LazyAIChatComponent } from './LazyAiChatComponent';
import {
  overlayPanelDesktop,
  overlayPanelElevated,
  overlayPanelStyles,
} from './OpalChatOverlay.css';

export function OpalChatOverlay() {
  const [isOpen, setIsOpen] = useAtom(opalOverlayOpenAtom);
  const [initialMessaging, setInitialMessaging] = useAtom(opalOverlayInitialMessagingAtom);
  const setChatBodyReady = useSetAtom(chatBodyReadyAtom);
  const { opalUser } = useOpalUser();
  const opalAuth = useAtomValue(opalAuthAtom);
  // `globalOptiHeaderVisibleAtom` is set false only while a full-screen `Portal`
  // takeover is open (e.g. the workflow execution / builder view). Lift the chat
  // above that layer in that case so it isn't occluded by the takeover.
  const headerVisible = useAtomValue(globalOptiHeaderVisibleAtom);
  const { pathname } = useLocation();
  const [chatFromFF, primaryAgentConfig] = useFeature('opal_app_features');
  const initialPrompt =
    (chatFromFF ? primaryAgentConfig.chat_initial_prompt?.initialPrompt : null) ||
    chatConfig.initialPrompt;

  const segments = pathname.split('/');
  const isOnOverlayPage = useIsOnOverlayPage();

  useEffect(() => {
    const onClosed = () => setIsOpen(false);
    window.addEventListener(EVENT_NAME.OPAL_CHATBOX_CLOSED, onClosed);
    return () => window.removeEventListener(EVENT_NAME.OPAL_CHATBOX_CLOSED, onClosed);
  }, [setIsOpen]);

  useEffect(() => {
    if (!isOnOverlayPage) {
      setIsOpen(false);
    }
  }, [isOnOverlayPage, setIsOpen]);

  // Drop any one-shot initial messaging once closed, so the next plain open (e.g.
  // the header button) restores the user's own last state instead of forcing chat.
  useEffect(() => {
    if (!isOpen) setInitialMessaging(undefined);
  }, [isOpen, setInitialMessaging]);

  // Body-liveness signal for Ask Opal: the same gate as the render guard below. READY
  // marks the federated body mounted; any drop of the gate (auth/user loss, a /chat
  // route) clears it even while the overlay atom stays open, so a later send never
  // targets a body whose SET_THREAD listener is gone.
  const bodyMounted = isOpen && !!opalAuth && !!opalUser && !segments.includes('chat');
  // useLayoutEffect (not useEffect): the federated body emits OPAL_CHAT_READY from its
  // own passive mount effect, and React runs passive effects children-before-parents.
  // A passive listener here would attach AFTER the child's emit whenever the chat chunk
  // is warm and the body mounts in the same commit the overlay opens, leaving
  // chatBodyReadyAtom stale-false forever (Ask Opal then waits on a READY that never
  // re-fires). Layout effects run before any passive effect, so the listener is armed
  // before the child can emit.
  useLayoutEffect(() => {
    // Reset first, unconditionally, so a fresh mount never inherits a prior lifecycle's
    // `true` (a full unmount runs cleanup but leaves the global atom as-is) — we only
    // trust THIS body's READY. Then: only listen while our own body is mounted. A
    // full-page /chat or /messages body also dispatches OPAL_CHAT_READY on window;
    // ignoring it (no listener attached while unmounted) keeps the atom from going
    // stale-true and sending into the restored thread. Our body always fires READY
    // after bodyMounted is already true.
    setChatBodyReady(false);
    if (!bodyMounted) return;
    const onReady = () => setChatBodyReady(true);
    window.addEventListener(EVENT_NAME.OPAL_CHAT_READY, onReady);
    return () => window.removeEventListener(EVENT_NAME.OPAL_CHAT_READY, onReady);
  }, [bodyMounted, setChatBodyReady]);

  if (!bodyMounted) return null;

  return (
    <div
      className={clsx(
        overlayPanelStyles,
        !headerVisible && overlayPanelElevated,
        isDesktopApp() && overlayPanelDesktop
      )}
    >
      <Suspense fallback={<FullScreenLoader />}>
        <LazyAIChatComponent
          currentUser={{
            instanceId: opalUser.instance.instance_id,
            avatarUrl: '',
            name: opalUser.firstname + ' ' + opalUser.lastname,
            operations: opalUser.instance.operations.filter(op => op.allowed).map(op => op.name),
          }}
          storeKey="overlay"
          productScope="OPAL"
          showHeaderIcons
          sidebarAlwaysVisible={false}
          initialDockState="docked"
          allowPopout={false}
          initialMessaging={initialMessaging}
          initialPrompt={initialPrompt}
          opalAuth={opalAuth}
        />
      </Suspense>
    </div>
  );
}
